import { useState, useEffect } from 'react';
import axios from 'axios';
import { Send, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const intendedUseOptions = [
  'Pharmaceutical R&D',
  'Cosmetic Formulation',
  'Supplement Manufacturing',
  'Academic Research',
  'Preclinical Studies',
  'API Sourcing / Distribution',
  'Other',
];

const volumeOptions = [
  '1-10g (Research)',
  '10-100g (Pilot)',
  '100g-1kg (Scale-up)',
  '1kg+ (Bulk / Commercial)',
  'Custom Requirement',
];

export default function RFQForm() {
  const [products, setProducts] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: '', contact_name: '', email: '', phone: '', country: '',
    product_interest: '', intended_use: '', volume_required: '', additional_notes: '',
    research_use_agreed: false,
  });

  useEffect(() => {
    axios.get(`${API}/products`).then(res => setProducts(res.data)).catch(() => {});
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email || !form.product_interest || !form.intended_use || !form.volume_required) {
      toast.error('Please fill in all required fields.');
      return;
    }
    if (!form.research_use_agreed) {
      toast.error('Please confirm the products are for research purposes only.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/rfq`, form);
      setSubmitted(true);
      toast.success('RFQ submitted successfully.');
    } catch {
      toast.error('Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="rfq" data-testid="rfq-section" className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-xl mx-auto text-center py-16">
            <CheckCircle2 className="w-12 h-12 text-[#198754] mx-auto mb-6" strokeWidth={1.5} />
            <h3
              className="text-2xl tracking-tight text-[#0A1128] mb-3"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}
              data-testid="rfq-success-title"
            >
              Request Received
            </h3>
            <p className="text-sm text-[#495057] mb-6" style={{ fontFamily: 'var(--font-body)' }}>
              Thank you for your inquiry. Our team will review your request and respond within 1-2 business days with a detailed quotation.
            </p>
            <button
              onClick={() => { setSubmitted(false); setForm({ company_name: '', contact_name: '', email: '', phone: '', country: '', product_interest: '', intended_use: '', volume_required: '', additional_notes: '', research_use_agreed: false }); }}
              className="btn-pill px-5 py-2.5 border border-[#DEE2E6] text-sm text-[#495057] hover:bg-[#F8F9FA] transition-colors duration-150"
              style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
              data-testid="rfq-new-request-btn"
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="rfq" data-testid="rfq-section" className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left column */}
          <div className="lg:col-span-4">
            <span
              className="text-xs tracking-widest uppercase text-[#0A7C84] mb-3 block"
              style={{ fontFamily: 'var(--font-mono)' }}
            >
              B2B Inquiries Only
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A1128] mb-6"
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 900 }}
              data-testid="rfq-heading"
            >
              Request
              <br />
              a Quote
            </h2>
            <p className="text-sm text-[#495057] leading-relaxed mb-8" style={{ fontFamily: 'var(--font-body)' }}>
              All inquiries are reviewed by our commercial team. We supply exclusively to registered businesses, research institutions, and licensed manufacturers.
            </p>
            <div className="space-y-4 border-t border-[#DEE2E6] pt-6">
              <div>
                <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2] block mb-0.5" style={{ fontFamily: 'var(--font-mono)' }}>Email</span>
                <span className="text-sm text-[#0A1128]" style={{ fontFamily: 'var(--font-mono)' }}>info@biolynpharma.com</span>
              </div>
              <div>
                <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2] block mb-0.5" style={{ fontFamily: 'var(--font-mono)' }}>Headquarters</span>
                <span className="text-sm text-[#0A1128]" style={{ fontFamily: 'var(--font-body)' }}>Amsterdam, The Netherlands</span>
              </div>
              <div>
                <span className="text-[10px] tracking-widest uppercase text-[#A0AAB2] block mb-0.5" style={{ fontFamily: 'var(--font-mono)' }}>Response Time</span>
                <span className="text-sm text-[#0A1128]" style={{ fontFamily: 'var(--font-body)' }}>1-2 Business Days</span>
              </div>
            </div>
          </div>

          {/* Right column - Form */}
          <div className="lg:col-span-8">
            <form onSubmit={handleSubmit} className="border border-[#DEE2E6] p-8 md:p-12" data-testid="rfq-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label className="text-xs tracking-wider uppercase text-[#6C757D] mb-2 block" style={{ fontFamily: 'var(--font-mono)' }}>
                    Company Name *
                  </Label>
                  <Input
                    className="rounded-none border-[#DEE2E6] bg-[#F8F9FA] focus:border-[#0A7C84] focus:ring-[#0A7C84]"
                    style={{ fontFamily: 'var(--font-body)' }}
                    value={form.company_name}
                    onChange={e => handleChange('company_name', e.target.value)}
                    placeholder="Acme Pharmaceuticals GmbH"
                    data-testid="rfq-company-name"
                  />
                </div>
                <div>
                  <Label className="text-xs tracking-wider uppercase text-[#6C757D] mb-2 block" style={{ fontFamily: 'var(--font-mono)' }}>
                    Contact Name *
                  </Label>
                  <Input
                    className="rounded-none border-[#DEE2E6] bg-[#F8F9FA] focus:border-[#0A7C84] focus:ring-[#0A7C84]"
                    style={{ fontFamily: 'var(--font-body)' }}
                    value={form.contact_name}
                    onChange={e => handleChange('contact_name', e.target.value)}
                    placeholder="Dr. Max Müller"
                    data-testid="rfq-contact-name"
                  />
                </div>
                <div>
                  <Label className="text-xs tracking-wider uppercase text-[#6C757D] mb-2 block" style={{ fontFamily: 'var(--font-mono)' }}>
                    Business Email *
                  </Label>
                  <Input
                    type="email"
                    className="rounded-none border-[#DEE2E6] bg-[#F8F9FA] focus:border-[#0A7C84] focus:ring-[#0A7C84]"
                    style={{ fontFamily: 'var(--font-body)' }}
                    value={form.email}
                    onChange={e => handleChange('email', e.target.value)}
                    placeholder="m.muller@acme-pharma.de"
                    data-testid="rfq-email"
                  />
                </div>
                <div>
                  <Label className="text-xs tracking-wider uppercase text-[#6C757D] mb-2 block" style={{ fontFamily: 'var(--font-mono)' }}>
                    Phone
                  </Label>
                  <Input
                    className="rounded-none border-[#DEE2E6] bg-[#F8F9FA] focus:border-[#0A7C84] focus:ring-[#0A7C84]"
                    style={{ fontFamily: 'var(--font-body)' }}
                    value={form.phone}
                    onChange={e => handleChange('phone', e.target.value)}
                    placeholder="+31 20 123 4567"
                    data-testid="rfq-phone"
                  />
                </div>
                <div>
                  <Label className="text-xs tracking-wider uppercase text-[#6C757D] mb-2 block" style={{ fontFamily: 'var(--font-mono)' }}>
                    Country
                  </Label>
                  <Input
                    className="rounded-none border-[#DEE2E6] bg-[#F8F9FA] focus:border-[#0A7C84] focus:ring-[#0A7C84]"
                    style={{ fontFamily: 'var(--font-body)' }}
                    value={form.country}
                    onChange={e => handleChange('country', e.target.value)}
                    placeholder="Netherlands"
                    data-testid="rfq-country"
                  />
                </div>
                <div>
                  <Label className="text-xs tracking-wider uppercase text-[#6C757D] mb-2 block" style={{ fontFamily: 'var(--font-mono)' }}>
                    Product of Interest *
                  </Label>
                  <Select value={form.product_interest} onValueChange={v => handleChange('product_interest', v)}>
                    <SelectTrigger className="rounded-none border-[#DEE2E6] bg-[#F8F9FA]" data-testid="rfq-product-interest">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none max-h-[300px]">
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                      ))}
                      <SelectItem value="Multiple Products">Multiple Products</SelectItem>
                      <SelectItem value="Custom Inquiry">Custom Inquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs tracking-wider uppercase text-[#6C757D] mb-2 block" style={{ fontFamily: 'var(--font-mono)' }}>
                    Intended Use *
                  </Label>
                  <Select value={form.intended_use} onValueChange={v => handleChange('intended_use', v)}>
                    <SelectTrigger className="rounded-none border-[#DEE2E6] bg-[#F8F9FA]" data-testid="rfq-intended-use">
                      <SelectValue placeholder="Select intended use" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {intendedUseOptions.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs tracking-wider uppercase text-[#6C757D] mb-2 block" style={{ fontFamily: 'var(--font-mono)' }}>
                    Volume Required *
                  </Label>
                  <Select value={form.volume_required} onValueChange={v => handleChange('volume_required', v)}>
                    <SelectTrigger className="rounded-none border-[#DEE2E6] bg-[#F8F9FA]" data-testid="rfq-volume-required">
                      <SelectValue placeholder="Select volume" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {volumeOptions.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mb-6">
                <Label className="text-xs tracking-wider uppercase text-[#6C757D] mb-2 block" style={{ fontFamily: 'var(--font-mono)' }}>
                  Additional Notes
                </Label>
                <textarea
                  className="w-full px-3 py-2 border border-[#DEE2E6] bg-[#F8F9FA] text-sm focus:border-[#0A7C84] focus:outline-none focus:ring-1 focus:ring-[#0A7C84] min-h-[100px] resize-y"
                  style={{ fontFamily: 'var(--font-body)' }}
                  value={form.additional_notes}
                  onChange={e => handleChange('additional_notes', e.target.value)}
                  placeholder="Specific purity requirements, delivery timeline, regulatory documentation needs..."
                  data-testid="rfq-additional-notes"
                />
              </div>

              <label className="flex items-start gap-3 mb-6 cursor-pointer select-none" data-testid="rfq-research-use-label">
                <input
                  type="checkbox"
                  checked={form.research_use_agreed}
                  onChange={e => handleChange('research_use_agreed', e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#0A7C84] shrink-0"
                  data-testid="rfq-research-use-checkbox"
                />
                <span className="text-xs text-[#495057] leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
                  I confirm I am at least 18 years of age and that all products are ordered for laboratory and research purposes only &mdash; not for human or veterinary consumption. *
                </span>
              </label>

              <div className="flex items-center justify-between border-t border-[#DEE2E6] pt-6">
                <p className="text-xs text-[#A0AAB2]" style={{ fontFamily: 'var(--font-mono)' }}>
                  * B2B inquiries only. No retail orders.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-pill group flex items-center gap-2 px-6 py-3 bg-[#0A7C84] text-white text-sm hover:bg-[#075E64] transition-colors duration-150 disabled:opacity-50"
                  style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
                  data-testid="rfq-submit-button"
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                  <Send className="w-4 h-4 transition-transform duration-150 group-hover:translate-x-0.5" strokeWidth={1.5} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
